import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD, formatDate, formatDateTime } from '../../../shared/format.js';
import { toast } from '../../../shared/useToast.js';
import { useShiftStatus } from '../../../shared/useShift.js';
import {
  useSupplierAccount,
  useCashboxes,
  useSettleSupplier,
  useRefundSupplier
} from '../hooks/useSupplierAccounts.js';
import { Modal, Row } from './Modal.jsx';

export const METHODS = [
  { key: 'cash', label: 'نقداً', emoji: '💵' },
  { key: 'transfer', label: 'تحويل', emoji: '🏦' },
  { key: 'cheque', label: 'صك', emoji: '🧾' },
  { key: 'card', label: 'بطاقة', emoji: '💳' },
  { key: 'other', label: 'أخرى', emoji: '📝' }
];

export const METHOD_LABELS = METHODS.reduce((a, m) => ({ ...a, [m.key]: m.label }), {});

export function PaymentStatusPill({ status }) {
  if (status === 'paid') return <Pill variant="success">مسددة بالكامل</Pill>;
  if (status === 'partial') return <Pill variant="warning">مسددة جزئياً</Pill>;
  if (status === 'cancelled') return <Pill variant="muted">ملغاة</Pill>;
  return <Pill variant="danger">غير مسددة</Pill>;
}

function todayLocalISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

function newRequestId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* ignore */ }
  return `rq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * نافذة «تسديد» — على مستوى المورد أو على فاتورة بعينها.
 *
 * منع التسديد المكرّر مبنيّ في الواجهة أيضاً: `requestId` واحد يُولَّد عند
 * فتح النافذة ويُرسل مع كل محاولة، فإن تكرّر الطلب (نقرة مزدوجة أو إعادة
 * إرسال) أعاد الخادم السند الأصلي بلا خصم ثانٍ. ومعه حارس إرسال محلي.
 */
export function SettleSupplierModal({ supplierId, invoice, mode = 'settle', onClose, onDone }) {
  const isRefund = mode === 'refund';
  const detail = useSupplierAccount(supplierId, { onlyDue: !isRefund });
  const cashboxesQ = useCashboxes();
  const settle = useSettleSupplier();
  const refund = useRefundSupplier();
  const { shift, blocked } = useShiftStatus();

  const mutation = isRefund ? refund : settle;
  const cashboxes = cashboxesQ.data || [];

  const summary = detail.data?.summary;
  const supplier = detail.data?.supplier;
  const invoices = detail.data?.invoices || [];

  // الفاتورة المختارة (إن وُجدت) تُقرأ من الخادم لا من صف القائمة القديم.
  const liveInvoice = useMemo(() => {
    if (!invoice) return null;
    const id = String(invoice._id || invoice.invoiceId || '');
    return invoices.find((i) => String(i._id) === id) || invoice;
  }, [invoice, invoices]);

  const maxAmount = isRefund
    ? Math.max(0, Number(summary?.creditBalance || 0))
    : (liveInvoice
      ? Math.max(0, Number(liveInvoice.due || 0))
      : Math.max(0, Number(summary?.remainingTotal || 0)));

  const totalDue = Number(summary?.remainingTotal || 0);
  const paidBefore = liveInvoice ? Number(liveInvoice.paid || 0) : Number(summary?.paidTotal || 0);
  const invoiceTotal = liveInvoice ? Number(liveInvoice.total || 0) : Number(summary?.purchasesTotal || 0);

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [cashboxId, setCashboxId] = useState('');
  const [paidAt, setPaidAt] = useState(todayLocalISO);
  const [notes, setNotes] = useState('');
  const requestIdRef = useRef(newRequestId());
  const submittingRef = useRef(false);

  useEffect(() => {
    if (maxAmount > 0 && amount === '') setAmount(maxAmount);
  }, [maxAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  // افتراض القاصة: درج الشفت المفتوح، وإلا أول قاصة نشطة.
  useEffect(() => {
    if (cashboxId || !cashboxes.length) return;
    const drawer = shift ? String(shift.cashboxId?._id || shift.cashboxId || '') : '';
    const found = drawer && cashboxes.find((c) => String(c._id || c.id) === drawer);
    setCashboxId(String((found || cashboxes[0])._id || (found || cashboxes[0]).id));
  }, [cashboxes, shift, cashboxId]);

  const value = Math.max(0, Math.round(Number(amount) || 0));
  const tooMuch = value > maxAmount;
  const needsCashbox = method === 'cash';
  const selectedBox = cashboxes.find((c) => String(c._id || c.id) === String(cashboxId));
  const boxBalance = selectedBox ? Number(selectedBox.currentBalance || 0) : 0;
  const insufficient = needsCashbox && !isRefund && !!selectedBox && value > boxBalance;
  const invalid = !value || tooMuch || (needsCashbox && !cashboxId) || insufficient;
  const after = Math.max(0, maxAmount - value);

  async function submit() {
    if (submittingRef.current) return;
    if (tooMuch) {
      toast.error(
        isRefund
          ? `المبلغ أكبر من الرصيد الدائن — المتاح ${formatIQD(maxAmount)}`
          : `المبلغ أكبر من المتبقي — المتبقي ${formatIQD(maxAmount)}`
      );
      return;
    }
    submittingRef.current = true;
    try {
      const res = await mutation.mutateAsync({
        supplierId: String(supplierId),
        invoiceId: liveInvoice ? String(liveInvoice._id) : undefined,
        amount: value,
        paymentMethod: method,
        cashboxId: needsCashbox ? cashboxId : undefined,
        paidAt: paidAt ? new Date(paidAt).toISOString() : undefined,
        notes: notes || undefined,
        requestId: requestIdRef.current
      });
      if (res?.duplicate) {
        toast.warning('هذا التسديد مسجَّل مسبقاً — لم يُخصم المبلغ مرتين');
      } else {
        toast.success(
          isRefund
            ? `تم تسجيل استرداد ${formatIQD(value)} وإصدار سند قبض`
            : res?.fullySettled
              ? `تم التسديد بالكامل (${formatIQD(value)}) وإصدار سند صرف`
              : `تم تسديد ${formatIQD(value)} — المتبقي ${formatIQD(res?.remainingTotal ?? after)}`
        );
      }
      if (onDone) onDone(res);
      onClose();
    } catch (_) {
      // الخطأ ظهر كتنبيه؛ النافذة تبقى مفتوحة ببياناتها.
      // معرّف جديد حتى لا يُحسب الطلب التالي تكراراً للفاشل.
      requestIdRef.current = newRequestId();
    } finally {
      submittingRef.current = false;
    }
  }

  const openInvoices = invoices.filter((i) => Number(i.due || 0) > 0);

  return (
    <Modal
      title={isRefund ? 'استرداد من المورد' : 'تسديد مستحقات المورد'}
      sub={[supplier?.name, liveInvoice ? `فاتورة ${liveInvoice.number}` : 'على الحساب (الأقدم فالأقدم)']
        .filter(Boolean).join(' — ')}
      onClose={onClose}
      width={600}
      footer={(
        <>
          <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إلغاء</button>
          <button
            type="button"
            className="btn btn-primary flex-1 justify-center"
            onClick={submit}
            disabled={invalid || mutation.isPending}
          >
            <Icon name="check" size={14} />
            {mutation.isPending
              ? 'جاري التنفيذ...'
              : `${isRefund ? 'تسجيل استرداد' : 'تسديد'} ${formatIQD(value)}`}
          </button>
        </>
      )}
    >
      {blocked && method === 'cash' ? (
        <div className="card p-3 text-[12.5px]" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          ⚠️ لا يوجد شفت مفتوح — الصرف النقدي يتطلب فتح شفت أولاً ليخرج المال من درج حقيقي ويظهر في تقرير الشفت.
        </div>
      ) : null}

      <div className="card p-3 space-y-1.5" style={{ background: '#FFFCF7' }}>
        <Row label="اسم المورد" value={supplier?.name || '—'} />
        {supplier?.phone ? <Row label="هاتف المورد" value={supplier.phone} /> : null}
        {liveInvoice ? <Row label="رقم الفاتورة" value={liveInvoice.number} /> : null}
        {liveInvoice ? <Row label="تاريخ الفاتورة" value={formatDate(liveInvoice.date)} /> : null}
        <Row label={liveInvoice ? 'إجمالي الفاتورة' : 'إجمالي المشتريات'} value={formatIQD(invoiceTotal)} />
        <Row label="المدفوع سابقاً" value={formatIQD(paidBefore)} color="#15803D" />
        <Row
          label={isRefund ? 'الرصيد الدائن المتاح' : (liveInvoice ? 'المتبقي على الفاتورة' : 'إجمالي المستحق')}
          value={formatIQD(maxAmount)}
          strong
          color={maxAmount > 0 ? '#B91C1C' : '#15803D'}
        />
        {!liveInvoice && !isRefund ? (
          <Row label="عدد الفواتير المستحقة" value={`${summary?.openInvoices || 0} فاتورة`} />
        ) : null}
        {!liveInvoice && !isRefund && totalDue !== maxAmount ? (
          <Row label="إجمالي المستحق على الحساب" value={formatIQD(totalDue)} />
        ) : null}
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">
          مبلغ التسديد الجديد <span className="text-red">*</span>
        </label>
        <input
          type="number"
          min="1"
          max={maxAmount}
          className="input"
          style={{ fontSize: 20, fontWeight: 800 }}
          value={amount}
          autoFocus
          onChange={(e) => setAmount(e.target.value === '' ? '' : Math.max(0, Math.round(Number(e.target.value) || 0)))}
          placeholder="0"
        />
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <button type="button" className="btn" onClick={() => setAmount(maxAmount)}>
            {isRefund ? 'كامل الرصيد الدائن' : 'تسديد كامل المتبقي'}
          </button>
          {[0.5, 0.25].map((r) => (
            <button key={r} type="button" className="btn" onClick={() => setAmount(Math.max(1, Math.round(maxAmount * r)))}>
              {r === 0.5 ? 'النصف' : 'الربع'}
            </button>
          ))}
        </div>
        {tooMuch ? (
          <div className="text-[12px] text-red font-semibold mt-1.5">
            لا يُقبل مبلغ أكبر من {isRefund ? 'الرصيد الدائن' : 'المتبقي'} ({formatIQD(maxAmount)}).
          </div>
        ) : (
          <div className="text-[11.5px] text-ink-3 mt-1.5">
            المتبقي بعد هذه الدفعة: <b>{formatIQD(after)}</b>
            {after === 0 && value > 0 && !isRefund ? ' — ستصبح الفاتورة مسددة بالكامل تلقائياً' : ''}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">تاريخ التسديد</label>
          <input
            type="datetime-local"
            className="input"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">المستخدم المنفّذ</label>
          <input className="input" value={shift?.cashierName || 'المستخدم الحالي'} readOnly style={{ background: '#FAF6EE' }} />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع <span className="text-red">*</span></label>
        <div className="flex gap-1.5 flex-wrap">
          {METHODS.map((m) => (
            <button
              key={m.key}
              type="button"
              className={`btn ${method === m.key ? 'btn-primary' : ''}`}
              onClick={() => setMethod(m.key)}
            >
              <span>{m.emoji}</span> {m.label}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-ink-3 mt-1.5">
          {method === 'cash'
            ? 'المبلغ يخرج من القاصة المختارة ويُختم بالشفت المفتوح عليها ويظهر في التدفق النقدي.'
            : 'يُدفع خارج الدرج النقدي (بنك/صك) فلا يُنقص رصيد أي قاصة — وتبقى ذمة المورد تنقص بالمبلغ.'}
        </div>
      </div>

      {method === 'cash' ? (
        <div>
          <label className="block text-xs font-semibold text-ink-2 mb-1">
            القاصة التي يُدفع منها <span className="text-red">*</span>
          </label>
          <select
            className={`input ${insufficient ? 'ring-1 ring-red' : ''}`}
            value={cashboxId}
            onChange={(e) => setCashboxId(e.target.value)}
          >
            <option value="">— اختر القاصة —</option>
            {cashboxes.map((cb) => {
              const id = String(cb._id || cb.id);
              return <option key={id} value={id}>{cb.name} — {formatIQD(cb.currentBalance || 0)}</option>;
            })}
          </select>
          {selectedBox && !isRefund ? (
            <div className="flex justify-between text-[11.5px] mt-1">
              <span className="text-ink-3">الرصيد بعد الدفع</span>
              <b style={{ color: insufficient ? '#B91C1C' : '#15803D' }}>{formatIQD(boxBalance - value)}</b>
            </div>
          ) : null}
          {insufficient ? (
            <div className="text-[11.5px] text-red mt-1 font-semibold">
              لا يمكن إتمام التسديد لعدم توفر رصيد كافٍ في القاصة
              ({formatIQD(boxBalance)} متوفر، والمطلوب {formatIQD(value)}).
            </div>
          ) : null}
        </div>
      ) : null}

      <div>
        <label className="block text-xs font-semibold text-ink-2 mb-1">ملاحظات</label>
        <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="اختياري" />
      </div>

      {!liveInvoice && !isRefund && openInvoices.length ? (
        <div>
          <div className="text-xs font-semibold text-ink-2 mb-1">
            سيُوزَّع المبلغ على الفواتير المستحقة (الأقدم فالأقدم)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-ink-3 border-b border-line text-right">
                  <th className="p-1.5">الفاتورة</th>
                  <th className="p-1.5">التاريخ</th>
                  <th className="p-1.5">الإجمالي</th>
                  <th className="p-1.5">المتبقي</th>
                  <th className="p-1.5">الحالة</th>
                </tr>
              </thead>
              <tbody>
                {openInvoices.slice(0, 8).map((i) => (
                  <tr key={String(i._id)} className="border-b border-line">
                    <td className="p-1.5 font-mono">{i.number}</td>
                    <td className="p-1.5 text-ink-3">{formatDate(i.date)}</td>
                    <td className="p-1.5">{formatIQD(i.total)}</td>
                    <td className="p-1.5 font-bold" style={{ color: '#B91C1C' }}>{formatIQD(i.due)}</td>
                    <td className="p-1.5"><PaymentStatusPill status={i.paymentStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {liveInvoice && (detail.data?.payments || []).some((p) => String(p.invoiceId) === String(liveInvoice._id)) ? (
        <div>
          <div className="text-xs font-semibold text-ink-2 mb-1">سندات سابقة على هذه الفاتورة</div>
          <div className="space-y-1">
            {(detail.data.payments || [])
              .filter((p) => String(p.invoiceId) === String(liveInvoice._id))
              .map((p) => (
                <div key={String(p._id)} className="flex items-center justify-between text-[12px] border-b border-line pb-1">
                  <span className="text-ink-3">{formatDateTime(p.paidAt)}</span>
                  <span className="font-mono">{p.voucherNumber}</span>
                  <span>{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</span>
                  <span className={p.status === 'reversed' ? 'line-through text-ink-3' : 'font-bold'}>
                    {formatIQD(p.amount)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
